import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import ContactEditor from "../components/employees/ContactEditor";
import {
    createDepartmentContact,
    deactivateContact,
    getOrganizationDepartment,
    updateContact,
    updateDepartmentLead,
} from "../services/hrOpsApi";
import { useAlertContext } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import "./OrganizationPage.css";

const DEPARTMENT_ADMIN_ROLES = ["superadmin", "admin", "manager", "senior_manager", "hr", "owner", "dev", "tech_admin", "director"];
const personInitials = value => String(value || "С").split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase();

export default function DepartmentDetailPage() {
    const { departmentId } = useParams();
    const { showAlert } = useAlertContext();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedLeadId, setSelectedLeadId] = useState("");
    const [savingLead, setSavingLead] = useState(false);
    const canManageDepartment = DEPARTMENT_ADMIN_ROLES.includes(user?.role);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const nextData = await getOrganizationDepartment(departmentId);
            setData(nextData);
            setSelectedLeadId(nextData?.lead_employee_id ? String(nextData.lead_employee_id) : "");
        } finally {
            setLoading(false);
        }
    }, [departmentId]);

    useEffect(() => {
        load().catch(error => showAlert(error.message, "error"));
    }, [load, showAlert]);

    const mutate = async (operation, successMessage) => {
        try {
            await operation();
            await load();
            showAlert(successMessage, "success");
            return true;
        } catch (error) {
            showAlert(error.message, "error");
            return false;
        }
    };

    const saveLead = async () => {
        setSavingLead(true);
        try {
            await mutate(
                () => updateDepartmentLead(departmentId, selectedLeadId ? Number(selectedLeadId) : null),
                selectedLeadId ? "Руководитель подразделения назначен" : "Руководитель подразделения снят",
            );
        } finally {
            setSavingLead(false);
        }
    };

    return <MainLayout><div className="department-page">
        <Link to="/organization" className="department-back">← Все подразделения</Link>
        {loading ? <div className="organization-state is-loading"><span/><p>Загружаем отдел…</p></div> : null}
        {!loading && data ? <>
            <header className="department-hero">
                <div><span>ПОДРАЗДЕЛЕНИЕ</span><h1>{data.name}</h1><p>Руководитель: <strong>{data.lead_name || "не назначен"}</strong></p></div>
                <div className="department-code">{data.code || personInitials(data.name)}</div>
            </header>
            <section className="department-overview" aria-label="Сводка по отделу">
                <div><span>Сотрудники</span><strong>{data.employees?.length || 0}</strong></div>
                <div><span>Активные контакты</span><strong>{(data.contacts || []).filter(contact => contact.is_active).length}</strong></div>
                <div><span>Руководитель</span><strong>{data.lead_name ? "Назначен" : "Не назначен"}</strong></div>
            </section>
            <section className="department-lead-panel" aria-labelledby="department-lead-title">
                <div className="department-lead-copy">
                    <span>УПРАВЛЕНИЕ ПОДРАЗДЕЛЕНИЕМ</span>
                    <h2 id="department-lead-title">Руководитель отдела</h2>
                    <p>Выберите руководителя из действующих сотрудников этого подразделения. Назначение можно изменить или снять в любой момент.</p>
                </div>
                {canManageDepartment ? <div className="department-lead-controls">
                    <label htmlFor="department-lead-select">Сотрудник</label>
                    <div>
                        <select id="department-lead-select" value={selectedLeadId} onChange={event => setSelectedLeadId(event.target.value)} disabled={savingLead}>
                            <option value="">Руководитель не назначен</option>
                            {data.lead_employee_id && !(data.employees || []).some(employee => employee.id === data.lead_employee_id)
                                ? <option value={data.lead_employee_id} disabled>{data.lead_name || "Текущий руководитель"} — больше не в подразделении</option>
                                : null}
                            {(data.employees || []).map(employee => <option key={employee.id} value={employee.id}>{employee.full_name}{employee.position ? ` — ${employee.position}` : ""}</option>)}
                        </select>
                        <button type="button" onClick={saveLead} disabled={savingLead || String(data.lead_employee_id || "") === selectedLeadId}>
                            {savingLead ? "Сохраняем…" : selectedLeadId ? "Назначить" : "Снять руководителя"}
                        </button>
                    </div>
                </div> : <div className="department-lead-readonly"><span>{personInitials(data.lead_name || "?")}</span><div><small>Текущий руководитель</small><strong>{data.lead_name || "Не назначен"}</strong></div></div>}
            </section>
            <section className="department-employees">
                <div className="department-section-heading"><div><span>КОМАНДА</span><h2>Сотрудники отдела</h2></div><b>{data.employees?.length || 0}</b></div>
                {data.employees?.length ? <div className="department-employee-grid">{data.employees.map(employee => <Link key={employee.id} to={`/employees/${employee.id}`} className={`department-employee-card${employee.id === data.lead_employee_id ? " is-lead" : ""}`}>
                    <span>{personInitials(employee.full_name)}</span><div><strong>{employee.full_name}</strong><small>{employee.position || "Должность не указана"}</small>{employee.id === data.lead_employee_id ? <em>Руководитель отдела</em> : null}</div><b aria-hidden="true">→</b>
                </Link>)}</div> : <div className="department-empty-team">В этом отделе пока нет сотрудников.</div>}
            </section>
            <div className="department-contacts"><ContactEditor departmentMode readOnly={!canManageDepartment} contacts={data.contacts || []}
                onCreate={payload => mutate(() => createDepartmentContact(departmentId, { ...payload, usage_type: "shared", allow_adaptation: false, is_primary: false }), "Контакты отдела обновлены")}
                onUpdate={(id, payload) => mutate(() => updateContact(id, payload), "Контакты отдела обновлены")}
                onDeactivate={id => mutate(() => deactivateContact(id), "Контакты отдела обновлены")} />
            </div>
        </> : null}
    </div></MainLayout>;
}
